import { Button, Icon } from "@diemmea/dma-design-system";
import { Dialog, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { useAuthStore } from "../../store/auth.store";
import { useNotificationStore } from "../../store/notification.store";
import tailwindMyConfig from "../../tailwind.my.config";
import { getSupportUrl } from "../../services/support";

function CheckBulletIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="#FA7959" />
      <path d="M9.868 15.145 7.42 12.697l-1.06 1.06 3.508 3.508 7.773-7.773-1.06-1.06-6.713 6.713Z" fill="#FCFCFA" />
    </svg>
  );
}

function AddonUpgradeContent() {
  const notificationStore = useNotificationStore();
  const authStore = useAuthStore((store) => store);

  const title = notificationStore.upgradePopupTitle || "Need an upgrade?";
  const description = notificationStore.upgradePopupSubtitle || "Unlock more features with the upgrade";
  const bulletPoints = notificationStore.upgradePopupBulletPoints || [];

  return (
    <DialogContent className="!overflow-x-hidden !p-0">
      <div className="flex max-w-[1024px] w-full flex-col md:flex-row">
        <div className="flex h-[180px] overflow-hidden bg-[#1f2937] md:h-auto md:min-h-[667px] md:w-[292px] md:shrink-0">
          <video
            className="block h-full w-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            aria-hidden="true"
          >
            <source src={"/videos/real-time-addon-loop.mp4"} type="video/mp4" />
          </video>
        </div>

        <div className="relative flex-1 overflow-x-hidden bg-[#FCFCFA] px-6 py-8 md:px-[60px] md:py-[56px]">
          <div className="pointer-events-none absolute left-1/2 top-[-311px] h-[796px] w-[1200px] -translate-x-1/2 bg-[radial-gradient(circle,rgba(250,121,89,0.13)_0%,rgba(250,121,89,0.06)_26%,rgba(252,252,250,0)_62%)]" />

          <button
            type="button"
            className="absolute right-6 top-6 z-10 text-secondaryDark transition-opacity hover:opacity-70 md:right-6 md:top-6"
            onClick={() => notificationStore.hideUpgradePopup()}
            aria-label="Close upgrade popup"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 15.32L15.32 4" stroke="#33475B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M15.32 15.32L4 4" stroke="#33475B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className="relative z-10 flex flex-col items-start">
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <span className="rounded-[62px] bg-black px-[11px] py-1 text-base font-semibold text-[#FCFCFA]">
                {"Add-on"}
              </span>
              <span
                className="text-base text-lightgray"
                style={{ fontFamily: "'Source Sans 3', 'Helvetica Neue', Arial, sans-serif" }}
              >
                {"Unlock this premium feature"}
              </span>
            </div>

            <h2
              className="mt-5 max-w-[408px] text-[32px] font-semibold italic leading-[1.1] text-secondaryDark md:text-[38px]"
              style={{ fontFamily: "'Fraunces', Georgia, serif" }}
            >
              {title}
            </h2>

            <p
              className="mt-6 max-w-[454px] text-[18px] leading-[1.35] text-secondaryDark"
              style={{ fontFamily: "'Source Sans 3', 'Helvetica Neue', Arial, sans-serif" }}
            >
              {description}
            </p>

            {bulletPoints.length > 0 ? (
              <ul className="mt-7 flex flex-col gap-4">
                {bulletPoints.map((bulletPoint) => (
                  <li
                    key={bulletPoint}
                    className="flex items-center gap-2 text-base leading-[1.35] text-secondaryDark"
                    style={{ fontFamily: "'Source Sans 3', 'Helvetica Neue', Arial, sans-serif" }}
                  >
                    <span className="mt-[1px] shrink-0">
                      <CheckBulletIcon />
                    </span>
                    <span>{bulletPoint}</span>
                  </li>
                ))}
              </ul>
            ) : null}

            <div className="flex flex-col items-center w-full max-w-[408px] gap-4">
              <button
                type="button"
                className="mt-8 w-full rounded-[9px] border border-primary bg-primary px-5 py-[10px] text-center text-sm font-bold leading-7 text-white transition-opacity hover:opacity-90"
                onClick={async () => {
                  notificationStore.showScheduleMeeting();
                }}
              >
                {"Talk to Sales"}
              </button>

              <Button
                isLoading={false}
                loadingText={"..."}
                className="w-min rounded-[5px] text-center text-sm font-semibold leading-7 text-secondaryDark"
                appearance="wrapper"
                onClick={() => {
                  window.open(getSupportUrl(authStore.user?.email), "_blank");
                  notificationStore.hideUpgradePopup();
                }}
              >
                {"I already have a paid subscription"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </DialogContent>
  );
}

function DefaultUpgradeContent() {
  const notificationStore = useNotificationStore();
  const authStore = useAuthStore((store) => store);

  const title = notificationStore.upgradePopupTitle || "Need an upgrade?";
  const subtitle = notificationStore.upgradePopupSubtitle || "Unlock more features with the upgrade";

  return (
    <>
      <DialogTitle className="relative bg-primary text-center text-xl font-bold text-white">
        <span>{title}</span>
        <button
          type="button"
          className="absolute right-[15px] top-[15px]"
          onClick={() => notificationStore.hideUpgradePopup()}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 15.32L15.32 4" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M15.32 15.32L4 4" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </DialogTitle>

      <DialogContent className="px-16 pb-10">
        <DialogContentText className="text-primary mt-8 mb-6 flex items-center justify-center text-base font-[600]">
          <Icon icon="premium" fill={tailwindMyConfig.theme.extend.colors.primary} />
          <span className="ml-2">{`This feature is included in the Paid subscription`}</span>
        </DialogContentText>
        <DialogContentText className="mb-8 text-center text-lg font-400 text-secondaryUltraDark">
          {subtitle}
        </DialogContentText>

        <div className="mt-3 flex items-center justify-center">
          <Button
            isLoading={false}
            loadingText={"..."}
            className="mr-3 w-auto"
            appearance="primary"
            onClick={async () => {
              notificationStore.showScheduleMeeting();
            }}
          >
            {"Talk to Sales"}
          </Button>

          <Button
            isLoading={false}
            loadingText={"..."}
            className="ml-3 w-auto"
            appearance="tertiary"
            onClick={() => {
              window.open(getSupportUrl(authStore.user?.email), "_blank");
              notificationStore.hideUpgradePopup();
            }}
          >
            {"I already have a paid subscription"}
          </Button>
        </div>
      </DialogContent>
    </>
  );
}

export default function UpgradeModal() {
  const notificationStore = useNotificationStore();
  const isAddonVariant = notificationStore.upgradePopupVariant === "addon";

  return (
    <Dialog
      open={notificationStore.isVisibleUpgradePopup}
      onClose={() => notificationStore.hideUpgradePopup()}
      TransitionProps={{
        onExited: () => {
          notificationStore.resetUpgradePopup();
        },
      }}
      className={`dialog-upgrade ${notificationStore.isVisibleScheduleMeeting && "dialog-upgrade-meeting"}`}
      BackdropProps={{ style: { filter: "blur(3px)", backdropFilter: "blur(3px)" } }}
      PaperProps={{
        className: isAddonVariant && !notificationStore.isVisibleScheduleMeeting
          ? "overflow-hidden rounded-2xl shadow-[0_24px_80px_rgba(51,71,91,0.18)]"
          : undefined,
      }}
      id="alert-dialog-upgrade-plan"
      maxWidth={isAddonVariant ? "lg" : "sm"}
      aria-labelledby="alert-start-dialog-upgrade-plan"
      aria-describedby="alert-start-dialog-upgrade-plan"
    >
      {notificationStore.isVisibleScheduleMeeting ? (
        <DialogContent className="px-16 pb-10">
          <iframe
            src="https://meetings.hubspot.com/alessandro-sandini/sync-hubspot-data-to-and-from-erps-with-alex-sandini-erp-bridge?uuid=4c37d1b5-048b-4f2d-ac06-43e4fbe48218&embed=true"
            title="Schedule a sales meeting"
            data-hs-ignore="true"
            style={{ border: "none", height: "100%", width: "100%", padding: 0, margin: 0 }}
          />
        </DialogContent>
      ) : isAddonVariant ? (
        <AddonUpgradeContent />
      ) : (
        <DefaultUpgradeContent />
      )}
    </Dialog>
  );
}
